import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useContext } from 'react';
import { AuthContext } from '../context/authContext.jsx';
import { myCompanyService } from '../services/companies/myCompanyService.js';
import { Eye, EyeSlash, SignInIcon } from '@phosphor-icons/react';



function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useContext(AuthContext);

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const loginResponse = await fetch(`/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!loginResponse.ok) {
        setError('Credenciales inválidas');
        setIsLoading(false);
        return;
      }

      const response = await loginResponse.json();
      const { token, sessionId, isFirstLogin } = response;

      // Guardar el token en las cookies (esto es CRÍTICO)
      Cookies.set('sessionToken', token, { expires: 7, path: '/' });

      if (sessionId) {
        Cookies.set('sessionId', sessionId, { expires: 7, path: '/' });
      }

      // Decodifica el token para obtener el rol
      let role = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));

        let companyName = '';
        try {
          const { company } = await myCompanyService.getMyCompany();
          companyName = company.name;
        } catch (companyErr) {
          console.error('Error fetching company:', companyErr);
        }

        const userData = { ...payload, companyName };

        // Guardar userData en localStorage para persistencia
        localStorage.setItem('userData', JSON.stringify(userData));

        // Actualizar el contexto de autenticación
        // isFirstLogin solo vive en memoria: dispara el recorrido de bienvenida una vez.
        login({ ...userData, isFirstLogin });

        role = payload.role;
      } catch (decodeErr) {
        setError('Error al decodificar el token');
        setIsLoading(false);
        return;
      }

      setTimeout(() => {
        // Redirige según el rol
        if (role === 'admin' || role === 'superadmin') {
          navigate('/admin/dashboard');
        } else if (role === 'technician') {
          navigate('/tecnico/dashboard');
        } else if (role === 'operator') {
          navigate('/admin/dashboard');
        } else {
          setError('Rol no autorizado');
          setIsLoading(false);
        }
      }, 500);

    } catch (err) {
      setError('Ocurrió un error inesperado');
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo y título */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-6">
            <img src="/generic-logo-white.png" alt="Sequela" className="h-16 w-auto" />
          </div>
          <h2 className="text-3xl font-bold text-content">Bienvenido</h2>
          <p className="mt-2 text-content">Sistema de Administración</p>
        </div>

        {/* Formulario de login */}
        <div className="bg-surface p-8 rounded-xl shadow-xl border border-line">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-error/12 border border-error rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <span className="text-error">⚠</span>
                  <span className="text-sm text-error">{error}</span>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-content mb-2">
                Correo Electrónico
              </label>
              <input
                id="email"
                type="email"
                className="w-full px-4 py-3 border border-line rounded-lg bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="tu@email.com"
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-content mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-4 py-3 pr-11 border border-line rounded-lg bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-content-muted hover:text-content"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-amber hover:bg-amber-hover disabled:bg-surface-2 text-on-amber disabled:text-content-muted py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-on-amber border-t-transparent rounded-full animate-spin" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <SignInIcon size={20} />
                  <span>Ingresar</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Información adicional */}
        <div className="text-center">
          <p className="text-xs text-content-muted">
            Sistema de gestión integral para equipos técnicos
          </p>
        </div>
      </div>

      {/* Decoración de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-amber/10 to-amber-hover/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-ink-2/10 to-ink-3/10 rounded-full blur-3xl" />
      </div>
    </div>
  );
}

export default AdminLogin;
